import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import api from "@/lib/api";
import { Lock, CreditCard } from "lucide-react";

// Public Stripe.js — no auth required, so we can load this on the /signup page.
let _pubStripePromise = null;
const getPublicStripe = () => {
  if (!_pubStripePromise) {
    _pubStripePromise = api.get("/payments/config").then(({ data }) => loadStripe(data.publishable_key));
  }
  return _pubStripePromise;
};

const CARD_OPTS = {
  style: {
    base: { fontSize: "16px", color: "#0f172a", fontFamily: "inherit", "::placeholder": { color: "#94a3b8" } },
    invalid: { color: "#dc2626" },
  },
};

function InnerForm({ email, name, disabled, onReadyChange, onError, formId, onSubmit }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardReady, setCardReady] = useState(false);

  // Expose the ability to trigger the card flow from the parent form's submit handler.
  useEffect(() => {
    if (!onSubmit) return;
    onSubmit.current = async () => {
      if (!stripe || !elements) {
        onError("The secure card form is still loading — please wait a moment and try again.");
        return null;
      }
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: { email: email || undefined, name: name || undefined },
      });
      if (error) {
        onError(error.message || "Your card could not be verified. Please check the details or try another card.");
        return null;
      }
      return paymentMethod.id;
    };
  }, [stripe, elements, email, name, onError, onSubmit]);

  useEffect(() => { if (onReadyChange) onReadyChange(cardReady && !!stripe); }, [cardReady, stripe, onReadyChange]);

  return (
    <div className={`rounded-xl border border-slate-200 bg-white px-4 py-3.5 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/30 transition-all ${disabled ? "opacity-60 pointer-events-none" : ""}`} data-testid="signup-card-element">
      <CardElement options={CARD_OPTS} onReady={() => setCardReady(true)} />
    </div>
  );
}

/**
 * SignupCardForm renders ONLY the Stripe CardElement (no button/label). The parent form
 * owns the submit action, error state, and network call — this component just exposes an
 * imperative `submitRef.current()` that returns a payment_method_id (or null on error).
 */
export function SignupCardForm({ email, name, disabled, onReadyChange, onError, submitRef }) {
  const [stripePromise] = useState(getPublicStripe);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    stripePromise
      .then((s) => { if (mounted && !s) setLoadErr(true); })
      .catch((e) => { console.error("[BidMatchAI] Stripe.js failed to load:", e?.message || e); if (mounted) setLoadErr(true); });
    return () => { mounted = false; };
  }, [stripePromise]);

  if (loadErr) return <p className="text-sm text-red-600" data-testid="stripe-load-error">Could not load the secure card form. Please refresh.</p>;
  return (
    <Elements stripe={stripePromise}>
      <InnerForm email={email} name={name} disabled={disabled} onReadyChange={onReadyChange} onError={onError} onSubmit={submitRef} />
    </Elements>
  );
}

export const SignupCardTrust = () => (
  <p className="text-xs text-slate-400 inline-flex items-center gap-1.5" data-testid="signup-card-trust">
    <Lock className="h-3.5 w-3.5" /> Your card is encrypted and processed securely by Stripe. We never store card numbers.
  </p>
);

export const SignupCardSubmit = ({ busy, disabled, children }) => (
  <button
    type="submit"
    disabled={disabled}
    data-testid="signup-submit-button"
    className="w-full bg-emerald-900 text-white rounded-xl py-3.5 text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
  >
    {busy ? "Processing…" : <><CreditCard className="h-4 w-4" /> {children}</>}
  </button>
);
